import React, { useEffect, useRef, useState } from "react";
import { API_URLS } from "../../utils/apiConfig.ts";

const ANON_KEY = "finq-wrap-anon-id";
const submittedKey = (wrapDate: string) => `finq-wrap-feedback-${wrapDate}`;
const ANON_PATTERN = /^[A-Za-z0-9._-]{8,64}$/;
const MAX_COMMENT = 500;

const REASONS: { id: string; label: string }[] = [
  { id: "missing_news", label: "Missing big news" },
  { id: "too_noisy", label: "Too much noise" },
  { id: "hard_to_read", label: "Hard to read" },
  { id: "wrong_numbers", label: "Wrong numbers" },
];

const getOrCreateAnonId = (): string => {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing && ANON_PATTERN.test(existing)) {
      return existing;
    }
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(ANON_KEY, id);
    return id;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const readSubmittedStars = (wrapDate: string): number | null => {
  try {
    const raw = localStorage.getItem(submittedKey(wrapDate));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { stars?: number };
    if (typeof parsed.stars === "number" && parsed.stars >= 1 && parsed.stars <= 5) {
      return parsed.stars;
    }
  } catch {
    /* ignore corrupt storage */
  }
  return null;
};

interface Props {
  wrapDate: string;
}

/**
 * End-of-wrap star rating. Anonymous, one vote per browser per day (overwrite on server).
 */
export const WrapFeedback: React.FC<Props> = ({ wrapDate }) => {
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    const prior = readSubmittedStars(wrapDate);
    setHover(0);
    setReasons([]);
    setComment("");
    setErrorMsg("");
    inFlight.current = false;
    if (prior != null) {
      setStars(prior);
      setStatus("done");
    } else {
      setStars(0);
      setStatus("idle");
    }
  }, [wrapDate]);

  const showReasons = stars > 0 && stars <= 3 && status !== "done";

  const toggleReason = (id: string) => {
    setReasons((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const submit = async (nextStars: number) => {
    if (inFlight.current || nextStars < 1 || nextStars > 5) {
      return;
    }
    inFlight.current = true;
    setStars(nextStars);
    setStatus("submitting");
    setErrorMsg("");
    try {
      const anonId = getOrCreateAnonId();
      const body: {
        stars: number;
        anonId: string;
        comment?: string;
        reasons?: string[];
      } = { stars: nextStars, anonId };
      if (nextStars <= 3) {
        const trimmed = comment.trim().slice(0, MAX_COMMENT);
        if (trimmed) body.comment = trimmed;
        if (reasons.length > 0) body.reasons = reasons;
      }

      const res = await fetch(API_URLS.WRAP_FEEDBACK(wrapDate), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error("save failed");
      }
      try {
        localStorage.setItem(submittedKey(wrapDate), JSON.stringify({ stars: nextStars }));
      } catch {
        /* ignore quota / private mode */
      }
      setStatus("done");
    } catch {
      setStatus("error");
      setErrorMsg("Could not save — try again.");
    } finally {
      inFlight.current = false;
    }
  };

  if (status === "done") {
    return (
      <div style={boxStyle}>
        <div style={titleStyle}>Thanks for rating</div>
        <div style={{ ...starsRowStyle, pointerEvents: "none" }} aria-label={`Rated ${stars} of 5`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} style={{ ...starStyle, color: n <= stars ? "#f59e0b" : "#cbd5e1" }}>
              ★
            </span>
          ))}
        </div>
        <div style={hintStyle}>Your feedback helps us improve the Daily Wrap.</div>
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      <div style={titleStyle}>Was this Daily Wrap useful?</div>
      <div style={starsRowStyle} role="radiogroup" aria-label="Rate this wrap">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = (hover || stars) >= n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={stars === n}
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              disabled={status === "submitting"}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(0)}
              onClick={() => {
                if (status === "submitting") return;
                if (n <= 3) {
                  setStars(n);
                  setStatus("idle");
                  setErrorMsg("");
                } else {
                  setReasons([]);
                  setComment("");
                  void submit(n);
                }
              }}
              style={{
                ...starBtnStyle,
                color: active ? "#f59e0b" : "#cbd5e1",
                cursor: status === "submitting" ? "wait" : "pointer",
              }}
            >
              ★
            </button>
          );
        })}
      </div>

      {showReasons && (
        <>
          <div style={{ ...hintStyle, marginTop: 14, marginBottom: 8 }}>What could be better?</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {REASONS.map((r) => {
              const on = reasons.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggleReason(r.id)}
                  style={{
                    border: on ? "1px solid #0f172a" : "1px solid #e2e8f0",
                    background: on ? "#0f172a" : "#fff",
                    color: on ? "#fff" : "#475569",
                    borderRadius: 999,
                    padding: "6px 12px",
                    fontFamily: "Neue Montreal, system-ui, sans-serif",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder="Optional comment"
            rows={2}
            maxLength={MAX_COMMENT}
            style={{
              marginTop: 12,
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "10px 12px",
              fontFamily: "Neue Montreal, system-ui, sans-serif",
              fontSize: 13,
              color: "#0f172a",
              resize: "vertical",
            }}
          />
          <button
            type="button"
            disabled={status === "submitting" || stars < 1}
            onClick={() => void submit(stars)}
            style={{
              marginTop: 12,
              background: "#0f172a",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "10px 18px",
              fontFamily: "Neue Montreal, system-ui, sans-serif",
              fontSize: 13,
              fontWeight: 600,
              cursor: status === "submitting" ? "wait" : "pointer",
              opacity: status === "submitting" ? 0.7 : 1,
            }}
          >
            {status === "submitting" ? "Sending…" : "Send feedback"}
          </button>
        </>
      )}

      {status === "error" && (
        <div style={{ ...hintStyle, color: "#b91c1c", marginTop: 10 }}>{errorMsg}</div>
      )}
    </div>
  );
};

const boxStyle: React.CSSProperties = {
  marginTop: 28,
  padding: "20px 18px",
  borderRadius: 14,
  border: "1px solid #e2e8f0",
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
  textAlign: "center",
};

const titleStyle: React.CSSProperties = {
  fontFamily: "Neue Montreal, system-ui, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  color: "#0f172a",
  marginBottom: 10,
};

const hintStyle: React.CSSProperties = {
  fontFamily: "Neue Montreal, system-ui, sans-serif",
  fontSize: 12,
  color: "#64748b",
  marginTop: 8,
};

const starsRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  gap: 4,
};

const starBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: "2px 4px",
  fontSize: 28,
  lineHeight: 1,
};

const starStyle: React.CSSProperties = {
  fontSize: 28,
  lineHeight: 1,
};

export default WrapFeedback;
